<template>
  <div>
    <p-data-table
      :data="gripAnalysisItems ? gripAnalysisItems : []"
      :hoverable="true"
      :noDataText="`No analysis found`"
      :class="$style.table"
    >
      <template slot="thead">
        <tr :class="$style.tableHeadRow">
          <th>
            <label class="b-checkbox checkbox">
              <input
                type="checkbox"
                v-model="isAllSelected"
                @click="selectAllAttributes()"
              />
              <span class="check"></span>
            </label>
          </th>
          <th
            width="40%"
            :class="[$style.tableHeader, $style.enableCursor]"
            @click="handleSort('analysisTitle')"
          >
            Analysis
            <div
              :class="sortOrder === 'ASC' ? $style.arrowUp : $style.arrowDown"
            />
          </th>
          <th :class="$style.tableHeader">Criteria</th>
          <th></th>
        </tr>
      </template>

      <template slot="tbody" slot-scope="{ data }">
        <tr v-for="item in data" :key="`active${item.id}`">
          <td>
            <label class="b-checkbox checkbox">
              <input
                type="checkbox"
                :value="item.id"
                v-model="selectedAnalysisIds"
                @input="toggleOption(item.id)"
              />
              <span class="check"></span>
            </label>
          </td>
          <td width="40%">
            <b-tooltip
              :active="item.title.length > 32"
              :label="item.title"
              position="is-right"
              size="is-medium"
              multilined
            >
              <span :class="$style.analysisTitle">{{ item.title }}</span>
            </b-tooltip>
          </td>
          <td
            v-if="getAnalysisCriteria(item).criteria"
            :class="$style.criteriaColumn"
          >
            <div :class="$style.criteriaTitle">
              <span :class="$style.documentId">{{
                getAnalysisCriteria(item).criteria.documentId
              }}</span>
              {{ getAnalysisCriteria(item).criteria.title }}
            </div>
            <span
              v-if="getAnalysisCriteria(item).moreCriteriaCount > 0"
              :class="$style.viewMore"
            >
              {{
                `+ ${getAnalysisCriteria(item).moreCriteriaCount} more criteria`
              }}
            </span>
          </td>
          <td v-else :class="$style.criteriaColumn">
            {{ '-' }}
          </td>
          <td>
            <b-dropdown
              hoverable
              aria-role="list"
              position="is-bottom-left"
              class="grip-drop-down-action"
            >
              <span class="icon has-text-primary" slot="trigger" role="button">
                <i class="fa fa-ellipsis-h"></i>
              </span>
              <b-dropdown-item
                aria-role="listitem"
                @click="handleViewDetails(item)"
                :class="$style.textButton"
              >
                View Details
              </b-dropdown-item>
            </b-dropdown>
          </td>
        </tr>
      </template>
    </p-data-table>

    <BasePagination
      v-if="gripAnalysisItems && gripAnalysisItems.length > 0"
      :page.sync="page"
      :perPage.sync="perPage"
      :totalNumberOfPage="totalNumberOfPage"
      :totalCount="totalCount"
      :isFirstPage="isFirstPage"
      :isLastPage="isLastPage"
      :startItemIndex="startItemIndex"
      :endItemIndex="endItemIndex"
      class="pagination-container"
    >
    </BasePagination>
  </div>
</template>

<script lang="ts">
import { mixins } from 'vue-class-component';
import { Component, Prop, Watch } from 'vue-property-decorator';
import BaseLoading from '../../../components/base/BaseLoading.vue';
import BasePagination from '../../../components/base/BasePagination.vue';
import { PaginationMixin } from '../../../components/base/pagination.mixin';
import { ProjectCriteria } from '../../../store/modules/projects/types/projects.types';
import { GripAnalysis } from '../../../store/types/grip.types';
import TableDropdown from '../../AdminProjectManagement/components/TableDropdown.vue';
import ViewGripAnalysisDetailsModal from './ViewGripAnalysisDetailsModal.vue';

@Component({
  components: {
    BasePagination,
    BaseLoading,
    TableDropdown,
    ViewGripAnalysisDetailsModal,
  },
})
export default class ExportGripReportModalAnalysisList extends mixins(
  PaginationMixin,
) {
  @Prop() public gripAnalysisItems!: GripAnalysis[];
  @Prop() public selectedGripAnalysisIds!: number[];
  @Prop(Number) public totalNumberOfPage!: number;
  @Prop(Number) public totalCount!: number;
  @Prop(Boolean) public isFirstPage!: boolean;
  @Prop(Boolean) public isLastPage!: boolean;
  @Prop(Number) public startItemIndex!: number;
  @Prop(Number) public endItemIndex!: number;
  @Prop(String) public sortColumn!: string;
  @Prop(String) public sortOrder!: 'ASC' | 'DESC';
  public isAllSelected: boolean = false;
  public selectedAnalysisIds: number[] = [];

  public mounted(): void {
    this.selectedAnalysisIds = this.selectedGripAnalysisIds;
    this.isAllSelected =
      this.selectedAnalysisIds.length === this.gripAnalysisItems.length;
  }

  public toggleOption(analysisId: number): void {
    /**
     * checking checkbox is selcted or not
     * if already selected removing from  selected Array
     * else adding to selected array
     */
    if (this.selectedAnalysisIds.includes(analysisId)) {
      this.selectedAnalysisIds = this.selectedAnalysisIds.filter(
        (id) => id !== analysisId,
      );
    } else {
      this.selectedAnalysisIds = [...this.selectedAnalysisIds, analysisId];
    }

    /** checking all analysis are selected or not */
    this.isAllSelected =
      this.selectedAnalysisIds.length === this.gripAnalysisItems.length;

    this.$emit('updateSelectedAnalysis', this.selectedAnalysisIds);
  }

  public selectAllAttributes(): void {
    if (this.isAllSelected) {
      this.selectedAnalysisIds = [];
      this.isAllSelected = false;
    } else {
      this.selectedAnalysisIds = this.gripAnalysisItems.map((item) => item.id);
      this.isAllSelected = true;
    }

    this.$emit(
      'updateSelectedAnalysis',
      this.selectedAnalysisIds,
      this.isAllSelected,
    );
  }

  public handleSort(column: string): void {
    this.$emit('sort', {
      sortColumn: column,
      sortOrder: this.sortOrder === 'ASC' ? 'DESC' : 'ASC',
    });
  }

  public getAnalysisCriteria(gripAnalysis: GripAnalysis) {
    const analysisCriteria: ProjectCriteria[] = gripAnalysis.gripAnalysisCriteriaMaps
      .filter((criteriaMap) => !criteriaMap.criteria.isDeleted)
      .map((criteriaMap) => criteriaMap.criteria);

    if (!analysisCriteria.length) {
      return { criteria: null, viewDetails: false, moreCriteriaCount: 0 };
    } else {
      return {
        criteria: analysisCriteria[0],
        viewDetails: true,
        moreCriteriaCount: analysisCriteria.length - 1,
      };
    }
  }

  public handleViewDetails(gripAnalysis: GripAnalysis): void {
    this.$buefy.modal.open({
      parent: this,
      component: ViewGripAnalysisDetailsModal,
      hasModalCard: true,
      trapFocus: true,
      props: {
        gripAnalysis,
      },
      events: {
        close: () => {
          this.$emit('close');
        },
      },
    });
  }

  @Watch('selectedAnalysisIds')
  public watchSelectedAnalysisIds(): void {
    this.$emit(
      'updateSelectedAnalysis',
      this.selectedAnalysisIds,
      this.isAllSelected,
    );
  }
}
</script>

<style lang="scss" module>
.tableHeadRow {
  border-bottom: 1px solid $grey-border;
}

.tableHeader {
  font-weight: bold !important;
  color: #444f51 !important;
}

.enableCursor {
  padding-left: 2rem;
  padding-right: 1.25rem;
  cursor: pointer;
}

.arrowUp {
  display: inline-block;
  vertical-align: middle;
  width: 12px;
  height: 15px;
  opacity: 0.66;
  opacity: 1;
  background-repeat: no-repeat;
  background-size: contain;
  background-image: url('~@/jbi-shared/assets/images/arrow_up.png');
  cursor: pointer;
}

.arrowDown {
  display: inline-block;
  vertical-align: middle;
  width: 12px;
  height: 15px;
  opacity: 0.66;
  opacity: 1;
  background-repeat: no-repeat;
  background-size: contain;
  background-image: url('~@/jbi-shared/assets/images/arrow_down.png');
  cursor: pointer;
}

.analysisTitle {
  max-width: 32ch;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.criteriaColumn {
  display: flex;
  line-height: 20px;

  .criteriaTitle {
    max-width: 60ch;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    text-align: initial;
  }

  .documentId {
    font-weight: 500;
    margin-right: 4px;
  }

  .viewMore {
    background-color: #eff7ff;
    border-radius: 4px;
    padding: 2px 4px;
    margin: 0 4px;
    font-size: 14px;
    white-space: nowrap;
  }
}

.textButton {
  color: #444f51 !important;
  font-weight: 400 !important;
  font-size: 14px;

  &:hover {
    text-decoration: none !important;
  }
}
</style>
